import React from 'react';
import styled from 'styled-components';
import { useField } from 'react-final-form';
import { Checkbox, CheckboxField } from '@qover/molecules';
import { Sitemap, Footnote } from '@qover/typography';
import messages from './priceTransparency.messages';

const ArrowTree = () => (
  <svg
    width="50px"
    height="50px"
    viewBox="0 0 50 50"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g
      id="Symbols"
      stroke="none"
      stroke-width="1"
      fill="none"
      fillRule="evenodd"
    >
      <g id="Icon/Save-the-planet" fill="#EE3D57">
        <path
          d="M31.4971223,7.85794147 C37.5930886,7.85794147 42.5559448,12.634665 42.7343971,18.5934815 L42.7387668,18.9481275 C46.8362754,19.4738168 50,22.9227866 50,27.0997018 C50,31.6401475 46.260971,35.3208569 41.6492524,35.3208569 C40.1723085,35.3208569 38.7849512,34.9430494 37.5812968,34.2803174 L37.2576528,34.0925778 L32.8328107,40.5683148 L33.0918159,48.6050322 C33.1135513,49.2794661 32.6237813,49.8516855 31.9721762,49.9487314 L31.7767092,49.9632046 L31.7767092,49.9632046 L31.2925642,49.9632046 C30.6177802,49.9632046 30.0616337,49.455257 29.985627,48.800864 L29.9770536,48.620326 L29.9770536,48.620326 L30.2015645,37.7175636 L25.9917503,32.2793479 C24.85941,32.9294572 23.542589,33.3018327 22.1374208,33.3018327 C17.904846,33.3018327 14.4736842,29.9242128 14.4736842,25.7573219 C14.4736842,22.2260935 16.9384186,19.2615362 20.2665587,18.4395387 C20.5247119,12.5525854 25.4535817,7.85794147 31.4971223,7.85794147 Z M34.12645,28.910573 C33.8574479,28.96444 33.5850701,29.0097105 33.3097854,29.0455525 L32.8947368,29.0921401 L33.1580424,36.8053099 L36.8421053,32.7808846 C35.6078603,31.7716586 34.6578082,30.4370469 34.12645,28.910573 Z M28.2863011,30.2263625 C27.836402,30.8513826 27.2816143,31.4169733 26.6423606,31.9065886 L26.3157895,32.1449885 L30.0481289,35.4895204 L30.2631579,30.5183338 C29.5877553,30.4628327 28.9278631,30.3639344 28.2863011,30.2263625 Z M3.6670704,0.203813775 C3.9143808,-0.0411669587 4.3055266,-0.0648622723 4.59491197,0.130357874 L4.69818178,0.213822462 L8.42218931,3.79625864 C8.48847363,3.86023935 8.5403095,3.93267173 8.57787516,4.00976484 C8.70766947,4.2757853 8.66731664,4.59859701 8.45218835,4.81559649 C8.20538974,5.06439864 7.81145104,5.08978622 7.52043464,4.89356422 L7.41661638,4.80959655 L5.0331045,2.51638319 C4.68434081,6.5243864 5.36544268,9.51971793 6.46015931,11.7687453 C7.7038872,14.3204594 9.49684057,15.9341191 11.0233709,16.9310482 C11.8620236,17.4783849 12.6187045,17.835283 13.1468624,18.0502878 L13.5934181,18.2197929 L14.0365383,18.3673327 C14.1983428,18.4428511 14.3253304,18.5711087 14.399705,18.7237419 C14.4709176,18.8698861 14.494325,19.0388139 14.4540381,19.2055383 C14.3704796,19.5509435 14.0426851,19.7688508 13.6909068,19.7329971 L13.5730612,19.7114741 C13.5203373,19.6967986 12.1439939,19.3281525 10.4071699,18.2287261 C8.67281425,17.1309013 6.57218189,15.2890156 5.14294442,12.3545161 C3.98082027,9.97189282 3.27451832,6.88413978 3.52756371,2.94753696 L3.5702936,2.37937512 L1.26079389,4.66597673 L1.14802028,4.75676361 C0.867608797,4.9374232 0.48095305,4.90127246 0.225308119,4.6518403 C-0.0341480542,4.3983392 -0.0705045405,4.00987102 0.121349246,3.73169093 L0.204001755,3.63302217 L3.6670704,0.203813775 Z"
          id="icon-save-the-planet"
        ></path>
      </g>
    </g>
  </svg>
);

const Container = styled.div`
  color: ${({ theme }) => theme.colors.textQuote()};
  max-width: 270px;
`;

const Environment = styled.div`
  color: ${({ theme }) => theme.colors.primary()};
  display: inline-block;
  line-height: 18px;
  padding-left: 61px;
  position: relative;

  svg {
    position: absolute;
    left: 5px;
    path {
      fill: ${({ theme }) => theme.colors.primary()};
    }
  }
`;

type PriceTransparencyCheckboxProps = {
  className?: string;
  disabled?: boolean;
  prefix: string;
  onChange?: (e: React.ChangeEvent<any>) => void;
};

export const PriceTransparencyCheckbox: React.FC<PriceTransparencyCheckboxProps> = props => {
  const isRequestPaperCopyChecked = useField(
    `${props.prefix}requestPaperCopy`,
    {
      subscription: { value: true },
      type: 'checkbox',
    },
  ).input.checked;

  return (
    <Container className={props.className}>
      <Checkbox
        data-test="general.requestEmailCopy"
        disabled
        id="requestEmailCopy"
        name="requestEmailCopy"
        checked
        valid
      >
        <Sitemap>{messages.receiveMyPolicyDocumentsEmail}</Sitemap>
      </Checkbox>
      <CheckboxField
        data-test="general.requestPaperCopy"
        disabled={props.disabled}
        id={`${props.prefix}requestPaperCopy`}
        name={`${props.prefix}requestPaperCopy`}
        onChange={props.onChange}
        optional
      >
        <Sitemap>{messages.receiveMyPolicyDocuments}</Sitemap>
      </CheckboxField>
      {isRequestPaperCopyChecked && !props.disabled && (
        <Environment>
          <ArrowTree />
          <Footnote className="font-bold">
            {messages.pleaseConsiderEnvironment}
          </Footnote>
        </Environment>
      )}
    </Container>
  );
};
